/**
 * Composizione della descrizione articolo mostrata nei preventivi (UI e PDF).
 *
 * PERCHE' ESISTE
 * Prima di [sc-48197] quattro punti diversi componevano la descrizione con
 * regole divergenti, quindi lo stesso articolo appariva in modo diverso a
 * seconda della schermata:
 *
 *   QuotePage.buildQuoteTable      itemName || subBrand || brand
 *   QuoteDetailPage.normalizeItems itemName || subBrand || brand
 *   EditQuotePage                  [brand, subBrand] uniti da " -> "
 *   CreateQuotePage (PDF)          [brand, subBrand] uniti da " "
 *
 * Per i sanitari la colonna "Tipo" del listino e' vuota, quindi itemName e'
 * nullo: le catene di fallback finivano per mostrare la sola serie. Il colore
 * non compariva da nessuna parte.
 *
 * REGOLA UNICA
 *   sanitari / altro (magazzino) -> "marca - serie - colore"
 *   piastrelle (magazzino)       -> itemName
 *   fuori magazzino piastrelle   -> "nome - misura"
 *
 * NB: gli articoli fuori magazzino non hanno la colonna "color" a DB, quindi
 * per loro il colore non e' disponibile (scelta deliberata: nessuna migrazione).
 */

/** Separatore unico fra i membri della descrizione, in UI e nei PDF. */
export const DESC_SEPARATOR = " - ";

const FALLBACK_STOCK = "Articolo";
const FALLBACK_OUTSTOCK = "Articolo fuori magazzino";

const lower = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

/** Unisce i membri non vuoti con il separatore comune. */
const join = (parts) =>
  parts
    .map((p) => String(p ?? "").trim())
    .filter(Boolean)
    .join(DESC_SEPARATOR);

/** Misura da mostrare: measureDesc se presente, altrimenti "LxH cm". */
export function itemMeasure(src = {}) {
  const explicit = String(src.measureDesc ?? "").trim();
  if (explicit) return explicit;
  const w = Number(src.widthCm ?? 0);
  const h = Number(src.heightCm ?? 0);
  return w > 0 && h > 0 ? `${src.widthCm}x${src.heightCm} cm` : "";
}

/**
 * Descrizione dell'articolo, identica in ogni schermata e nei PDF.
 * @param {object} src articolo gia' risolto (Item oppure Outstock)
 * @param {{ withMeasure?: boolean }} [options] withMeasure aggiunge la misura
 *   alle piastrelle di magazzino. Unico chiamante: il PDF di CreateQuotePage,
 *   che gia' prima del refactor componeva "nome misura". Altrove NON va usata:
 *   la misura non faceva parte del titolo e vive in una colonna/meta separata.
 *   Non influisce sui fuori magazzino, che hanno una regola propria.
 * @returns {string}
 */
export function itemTitle(src, options = {}) {
  if (!src || typeof src !== "object") return FALLBACK_STOCK;

  const { withMeasure = false } = options;
  const category = lower(src.category);
  const isOutstock = category === "fuori magazzino";

  // --- FUORI MAGAZZINO ---
  if (isOutstock) {
    // Le piastrelle fuori magazzino aggiungono la misura; le altre no.
    const measure =
      lower(src.subCategory) === "piastrelle" ? itemMeasure(src) : "";
    return (
      join([src.itemName, measure]) ||
      join([src.brand, src.subBrand]) ||
      FALLBACK_OUTSTOCK
    );
  }

  // --- PIASTRELLE DI MAGAZZINO ---
  // Il colore non e' valorizzato per le piastrelle: resta fuori dalla descrizione.
  if (category === "piastrelle") {
    const measure = withMeasure ? itemMeasure(src) : "";
    return join([src.itemName, measure]) || FALLBACK_STOCK;
  }

  // --- SANITARI / ALTRO DI MAGAZZINO ---
  // Qui itemName e' tipicamente vuoto: la descrizione utile e' marca/serie/colore.
  return (
    join([src.brand, src.subBrand, src.color]) ||
    String(src.itemName ?? "").trim() ||
    FALLBACK_STOCK
  );
}

/**
 * Nome breve del fuori magazzino per liste e modali gestionali (SENZA misura):
 *   piastrelle       -> itemName
 *   sanitari / altro -> "marca - modello" (brand - subBrand)
 * A differenza di itemTitle(), non aggiunge la misura alle piastrelle: nella
 * pagina fuori magazzino la misura vive gia' in una colonna dedicata, quindi
 * ripeterla nel nome sarebbe una duplicazione.
 * @param {object} src articolo fuori magazzino risolto
 * @returns {string}
 */
export function outstockName(src) {
  if (!src || typeof src !== "object") return FALLBACK_OUTSTOCK;
  return (
    String(src.itemName ?? "").trim() ||
    join([src.brand, src.subBrand]) ||
    FALLBACK_OUTSTOCK
  );
}

/**
 * Qualificatore che distingue articoli con la stessa "marca - serie - colore":
 * tipo (vaso/bidet/...) e posizione (sospeso/a terra/...).
 * Vuoto per piastrelle e fuori magazzino, che non hanno questi campi.
 */
export function itemQualifier(src = {}) {
  if (!src || typeof src !== "object") return "";
  const cap = (v) => {
    const s = String(v ?? "").trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
  };
  const type = cap(src.fixtureType ?? src.type);
  const rawPos = String(src.fixturePosition ?? src.position ?? "").trim();
  const position = rawPos === "terra" ? "a terra" : cap(rawPos);
  return [type, position].filter(Boolean).join(" ");
}

/**
 * Descrizione completa dell'articolo: titolo + tipo/posizione (qualificatore).
 *
 * Per i sanitari 22 voci su 34 condividono "marca - serie - colore" e si
 * distinguono solo per tipo e posizione (vaso/bidet, sospeso/terra): senza il
 * qualificatore la stessa riga apparirebbe identica in tabella e nel PDF. Per
 * piastrelle e fuori magazzino itemQualifier() e' vuoto, quindi il risultato
 * coincide con itemTitle() e nulla cambia.
 * @param {object} src articolo gia' risolto (Item oppure Outstock)
 * @param {{ withMeasure?: boolean }} [options] inoltrato a itemTitle()
 * @returns {string} es. "SDR - BULL - panna - Vaso sospeso"
 */
export function itemFullDescription(src, options = {}) {
  return join([itemTitle(src, options), itemQualifier(src)]);
}

/**
 * Etichetta per i MENU DI SELEZIONE articolo e per il testo scritto nel campo
 * di ricerca dopo la scelta. E' la descrizione completa con la misura inclusa
 * (le piastrelle a menu mostrano anche la misura).
 *
 * @param {object} src articolo gia' risolto (Item oppure Outstock)
 * @returns {string} es. "GEBERIT - SELNOVA PREMIUM - bianco - Vaso sospeso"
 */
export function itemPickerLabel(src) {
  if (!src || typeof src !== "object") return "";
  return itemFullDescription(src, { withMeasure: true });
}

export default itemTitle;
